import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Typography,
    CircularProgress,
    Select,
    MenuItem,
    Button,
    SelectChangeEvent,
    ListSubheader,
    useAppDispatch,
    useAppSelector,
} from '../../base';
import {
    PageContainer,
    ContentWrapper,
    ErrorContainer,
    LoadingContainer,
    LogoSection,
    HeaderSection,
    PurpleSection,
    SelectWrapper,
    FooterSection,
    PargodaBranding,
} from './ScheduleInterviewPage.styles';
import {
    scheduleInterviewActions,
    scheduleInterviewSelectors,
} from '../../actions';

const ScheduleInterviewPage = () => {
    const dispatch = useAppDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>('');
    const tokenRef = useRef<string | null>(null);

    const interviewData = useAppSelector(
        scheduleInterviewSelectors.getScheduleInterviewData
    );
    const error = useAppSelector(
        scheduleInterviewSelectors.getScheduleInterviewError
    );
    const isLoading = useAppSelector(
        scheduleInterviewSelectors.getIsFetchLoading
    );
    const isSubmitting = useAppSelector(
        scheduleInterviewSelectors.getIsSubmitLoading
    );

    useEffect(() => {
        const token = searchParams.get('token');

        if (!token) {
            dispatch(
                scheduleInterviewActions.setScheduleInterviewError(
                    'Invalid access. Token is required.'
                )
            );
            return;
        }

        tokenRef.current = token;
        setSearchParams({}, { replace: true });
        dispatch(scheduleInterviewActions.fetchScheduleInterview({ token }));

        return () => {
            dispatch(scheduleInterviewActions.clearScheduleInterview());
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTimeSlotChange = (event: SelectChangeEvent<unknown>) => {
        setSelectedTimeSlot(event.target.value as string);
    };

    const handleSubmit = () => {
        if (!selectedTimeSlot || !tokenRef.current) {
            return;
        }

        const [date, time] = selectedTimeSlot.split('_');
        dispatch(
            scheduleInterviewActions.submitScheduleInterview({
                token: tokenRef.current,
                date,
                time,
            })
        );
    };

    if (isLoading) {
        return (
            <PageContainer>
                <LoadingContainer>
                    <CircularProgress size={60} />
                    <Typography variant="h6" sx={{ mt: 2 }}>
                        Loading interview details...
                    </Typography>
                </LoadingContainer>
            </PageContainer>
        );
    }

    if (error || !interviewData) {
        return (
            <PageContainer>
                <ErrorContainer>
                    <Typography variant="h4" gutterBottom>
                        Oops! Something went wrong
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {error ||
                            "You've reached the wrong page. Please check your link and try again."}
                    </Typography>
                </ErrorContainer>
            </PageContainer>
        );
    }

    const hasNoSlots =
        !interviewData.available_slots ||
        interviewData.available_slots.length === 0;
    const slotsUnavailableMessage =
        interviewData.error ||
        'No available time slots at the moment. Please contact the recruiter.';

    return (
        <PageContainer>
            <ContentWrapper>
                <LogoSection>
                    <Typography
                        variant="h3"
                        sx={{
                            color: '#7C3AED',
                            fontWeight: 700,
                            letterSpacing: '0.1em',
                        }}
                    >
                        {interviewData.company_name}
                    </Typography>
                </LogoSection>

                <HeaderSection>
                    <Typography variant="h5" gutterBottom>
                        Hi <strong>{interviewData.candidate_name}</strong>,
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                        We would like to schedule your interview for the{' '}
                        <strong>{interviewData.job_title}</strong> position at{' '}
                        <strong>{interviewData.company_name}</strong>.
                    </Typography>
                </HeaderSection>

                <PurpleSection>
                    {hasNoSlots ? (
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ textAlign: 'center', py: 2 }}
                        >
                            {slotsUnavailableMessage}
                        </Typography>
                    ) : (
                        <>
                            <SelectWrapper>
                                <Select
                                    value={selectedTimeSlot}
                                    onChange={handleTimeSlotChange}
                                    displayEmpty
                                    fullWidth
                                    sx={{
                                        backgroundColor: 'white',
                                        borderRadius: 1,
                                        '& .MuiSelect-select': {
                                            padding: '12px 14px',
                                        },
                                    }}
                                >
                                    <MenuItem value="" disabled>
                                        Choose a time
                                    </MenuItem>
                                    {interviewData.available_slots.map(
                                        (slot) => [
                                            <ListSubheader key={slot.date}>
                                                {slot.display_date}
                                            </ListSubheader>,
                                            ...slot.times.map((time) => (
                                                <MenuItem
                                                    key={`${slot.date}_${time.value}`}
                                                    value={`${slot.date}_${time.value}`}
                                                >
                                                    {time.label}
                                                </MenuItem>
                                            )),
                                        ]
                                    )}
                                </Select>
                            </SelectWrapper>
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={handleSubmit}
                                disabled={!selectedTimeSlot || isSubmitting}
                                sx={{
                                    padding: '12px 40px',
                                    borderRadius: '24px',
                                    textTransform: 'none',
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    '&.Mui-disabled': {
                                        backgroundColor: 'secondary.light',
                                        color: 'secondary.contrastText',
                                        opacity: 0.6,
                                    },
                                }}
                            >
                                {isSubmitting ? 'Submitting...' : 'Submit'}
                            </Button>
                        </>
                    )}
                </PurpleSection>

                <FooterSection>
                    <PargodaBranding>
                        <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary' }}
                        >
                            Powered by
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: '#7C3AED',
                                fontWeight: 700,
                                letterSpacing: '0.05em',
                            }}
                        >
                            PARGODA
                        </Typography>
                    </PargodaBranding>
                </FooterSection>
            </ContentWrapper>
        </PageContainer>
    );
};

export default ScheduleInterviewPage;
